/*
 *   Copyright (c) 2021 RCUBE PLANET PVT LTD
 *   All rights reserved.
 */
import React, { createContext, useEffect, useRef, useState } from 'react'
import { useCookies } from 'react-cookie'
import { history } from '../history'
import { ToastContainer, toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

const checkIsAuthenticated = async (token: string, path: string) => {
  return await fetch(`${process.env.REACT_APP_API_DOMAIN}${path}`, {
    method: 'POST',
    mode: 'cors',
    cache: 'no-cache',
    headers: {
      'Content-Type': 'application/json',
      'accept': 'application/json',
      'Authorization': 'Bearer ' + token,
    },
    redirect: 'follow',
    referrerPolicy: 'no-referrer',
    body: JSON.stringify({}),
  })
}
const authLogin = async (url: string, credentials: {}) => {
  return await fetch(`${process.env.REACT_APP_API_DOMAIN}${url}`, {
    method: 'POST',
    mode: 'cors',
    cache: 'no-cache',
    headers: {
      'Content-Type': 'application/json',
      'accept': 'application/json',
    },
    redirect: 'follow',
    referrerPolicy: 'no-referrer',
    body: JSON.stringify({
      ...credentials,
    }),
  })
}

const authLogout = async (url: string, token: string) => {
  return await fetch(`${process.env.REACT_APP_API_DOMAIN}${url}`, {
    method: 'POST',
    mode: 'cors',
    cache: 'no-cache',
    headers: {
      'Content-Type': 'application/json',
      'accept': 'application/json',
      'Authorization': 'Bearer ' + token,
    },
    redirect: 'follow',
    referrerPolicy: 'no-referrer',
    body: JSON.stringify({}),
  })
}
interface propType {}

export const AuthContext = createContext<{
  isAuthenticated: boolean
  isLoading: boolean
  login: (param: any) => any
  logout: () => void
  user: userType | null
  recentSearchRef: React.MutableRefObject<RecentSearchesType | null>
  unreadNotification: number
  checkAuth: () => void
  checkAuthStateless: () => void
  http: <bodyType extends {}, responseType extends {}>(
    path: string,
    body: bodyType
  ) => Promise<{
    json: responseType
  }>
}>(null as any)
export const Auth: React.FC<propType> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [cookies, setCookie, removeCookie] = useCookies(['user_type', 'token'])
  const [user, setUser] = useState<userType | null>(null)
  const [unreadNotification, setUnreadNotification] = useState<number>(0)
  const recentSearchRef = useRef<RecentSearchesType | null>(null)

  const checkAuthStateless = () => {
    //don't use setState here
    //only ref+
    checkIsAuthenticated(cookies.token, '/check')
      .then(async (response) => {
        const jsonResult = (await response.json()) as httpResponses['check']['response']

        if (jsonResult?.code !== 200) {
          if (jsonResult?.error === 'E_UNAUTHORIZED_ACCESS: ACCOUNT_INACTIVE') {
            alert('Your account is currently under review.')
          }
          throw jsonResult?.errorTrace?.[0]?.message || jsonResult.error
        }
        recentSearchRef.current = jsonResult?.recent_searches || null
        console.log('recentSearchRef', recentSearchRef.current)
      })
      .catch((err) => {
        console.log(err || 'Something went wrong!')
      })
  }

  // const getCheckRefData = () => {}

  useEffect(() => {
    checkAuth()
  }, [cookies.token])

  const checkAuth = () => {
    setIsLoading(true)
    console.log('token is ', cookies.token)
    checkIsAuthenticated(cookies.token, '/check')
      .then(async (response) => {
        const jsonResult = (await response.json()) as httpResponses['check']['response']

        if (jsonResult?.code !== 200) {
          if (jsonResult?.error === 'E_UNAUTHORIZED_ACCESS: ACCOUNT_INACTIVE') {
            alert('Your account is currently under review.')
          }
          throw jsonResult?.errorTrace?.[0]?.message || jsonResult.error
        }
        setIsAuthenticated(true)
        setIsLoading(false)
        setUser(jsonResult?.staff || null)
        setUnreadNotification(jsonResult.unreadNotifications)
        recentSearchRef.current = jsonResult?.recent_searches || null
      })
      .catch((err) => {
        if (typeof err !== 'string') {
          err = 'Network error'
        }
        console.log(err || 'Something went wrong!')

        setIsLoading(false)
        setIsAuthenticated(false)
        removeCookie('token')
        removeCookie('user_type')
        setUser(null)
      })
  }
  const login = (credentials: {}) => {
    setIsLoading(true)
    authLogin('/login', credentials)
      .then(async (response) => {
        const jsonResult = await response.json()
        console.log('jsonResult', jsonResult)

        if (jsonResult?.code !== 200) {
          const error = jsonResult?.errorTrace?.[0]?.message || jsonResult?.error
          throw error
        }

        setIsAuthenticated(true)
        setIsLoading(false)
        setCookie('token', jsonResult?.token.token, { path: '/' })

        history.push('/')
      })
      .catch((err) => {
        if (typeof err !== 'string') {
          err = 'Network error'
        }

        ;(toast as any).warning('Invalid email/password')

        setIsLoading(false)
        setIsAuthenticated(false)
        removeCookie('token')
        removeCookie('user_type')
        setUser(null)
      })
  }

  const logout = () => {
    setIsLoading(true)
    authLogout('/logout', cookies.token)
      .then(async (response) => {
        const jsonResult = await response.json()
        console.log('jsonResult', jsonResult)

        if (jsonResult?.code !== 200) {
          const error = jsonResult?.errorTrace?.[0]?.message || jsonResult?.error
          throw error
        }
        console.log('Logout successful!')
      })
      .catch((err) => {
        if (typeof err !== 'string') {
          err = 'Network error'
        }
        console.log(err || 'Something went wrong!')
      })
      .finally(() => {
        setIsLoading(false)
        setIsAuthenticated(false)
        removeCookie('token')
        setUser(null)

        history.push('/login')
      })
  }

  const http = async <bodyType extends {}, responseType extends {}>(
    path: string,
    body: bodyType
  ): Promise<{
    json: responseType
  }> => {
    //console.log('log of http ', JSON.stringify(body))
    const url = path.includes('://') ? path : `${process.env.REACT_APP_API_DOMAIN}${path}`
    const response = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      cache: 'no-cache',
      headers: {
        'Content-Type': 'application/json',
        'accept': 'application/json',
        'Authorization': 'Bearer ' + cookies.token,
      },

      redirect: 'follow',
      referrerPolicy: 'no-referrer',
      body: JSON.stringify(body),
    })

    const data = await response.json()

    if (data.code === 500 && data.internalCode === 'E_INVALID_ID_API_TOKEN') {
      removeCookie('token')
      removeCookie('user_type')
      history.push('/login')
      throw new Error('Invalid Token')
    }

    return { json: data }
  }

  console.log('isLoading', isLoading)
  return (
    <>
      <AuthContext.Provider
        value={{
          isAuthenticated,
          isLoading,
          login,
          logout,
          user,
          checkAuth,
          http,
          unreadNotification,
          recentSearchRef,
          checkAuthStateless,
        }}
      >
        <ToastContainer></ToastContainer>
        {children}
      </AuthContext.Provider>
    </>
  )
}

interface httpResponses {
  check: {
    response: {
      code: 200 | 500
      error?: string
      errorTrace?: any
      staff?: userType
      unreadNotifications: number
      recent_searches?: RecentSearchesType
    }
  }
}
export type RecentSearchesType = {
  country?: {
    type: string
    payload: {
      orderBy: string
      orderDirection: string
    } & Partial<{
      name: string
      code: string
    }>
    staff_id: number
  }

  vendor_plan?: {
    type: string
    payload: {
      orderBy: string
      orderDirection: string
    } & Partial<{
      operator_id: number
      amount: number
      vendor_id: number
      circles: number[]
    }>
    staff_id: number
  }
  vendor_defination?: {
    type: string
    payload: {
      orderBy: string
      orderDirection: string
    } & Partial<{
      operator_id: number
      circle_id: number
      vendor_id: number
    }>
    staff_id: number
  }
  vendor_notification?: {
    type: string
    payload: {
      orderBy: string
    } & Partial<{
      orderDirection: string
      type: string
      has_read: boolean
      operator_id: number
      category_id: number
      amount: number
      vendor_ids: number[]
      circle_id: number
      real_time_plan_exist: boolean
      batch_id: number
    }>
    staff_id: number
  }
  vendor_notification_group?: {
    type: string
    payload: {
      orderBy: string
    } & Partial<{
      orderDirection: string
      type: string
      has_read: boolean
      operator_id: number
      category_id: number
      amount: number
      vendor_ids: number
      circle_id: number
      real_time_plan_exist: boolean
      batch_id: number
    }>
    staff_id: number
  }
  plan?: {
    type: string
    payload: {
      orderBy: string
      orderDirection: string
    } & Partial<{
      operator_id: number
      circle_id: number
      category_id: number
      amount: number
    }>
    staff_id: number
  }
}

type userType = {
  id: number
  email: string
  mobile: string
  name: string
  role: string
  remember_me_token: string
  is_active: number
  pin: number
  created_at: string
  updated_at: string
  operator_ids?: number[]
  $staff_operator_links: {
    operator_id: number
    staff_id: number
    $operator_meta: {
      name: string
      id: number
    }
  }[]
  $recent_searches: {
    type: string
    payload: any
    staff_id: number
  }[]
}
